import { api } from "./client";
import { useResource } from "./useResource";
import { useRealtimeScope } from "../realtime/useRealtimeScope";

// GET /api/v1/tasks/:id/pull-request — the task's pull request, plus the state
// of an open-PR request that has not produced one (yet, or ever).
export type ApiPullRequest = {
  readonly base_branch: string;
  readonly closed_at: string | null;
  /**
   * The branch conflicts with its base — GitHub's verdict, resolved
   * server-side. Which of its `mergeable_state` values mean this is provider
   * detail the dashboard deliberately does not know.
   */
  readonly conflicted: boolean;
  readonly head_branch: string;
  readonly head_sha: string;
  readonly html_url: string | null;
  readonly last_synced_at: string | null;
  /**
   * Armed with "merge when ready": the server merges this pull request itself
   * once every check that reported for its head is green and the branch does
   * not conflict with its base.
   */
  readonly merge_when_ready: boolean;
  readonly merged_at: string | null;
  readonly number: number;
  readonly public_id: string;
  readonly state: string;
  readonly title: string;
};

/**
 * An open-PR or merge attempt that has not settled successfully. `queued` is
 * still trying (`attempts` of `max_attempts` spent); `failed` has given up and
 * `error` says why.
 */
export type ApiPullRequestAttempt = {
  readonly attempts: number;
  readonly error: string | null;
  readonly max_attempts: number;
  readonly status: string;
};

type PullRequestResponse = {
  readonly merge_request: ApiPullRequestAttempt | null;
  readonly pull_request: ApiPullRequest | null;
  readonly rebase_request: ApiPullRequestAttempt | null;
  readonly request: ApiPullRequestAttempt | null;
};

export type MergeStrategy = "merge" | "rebase" | "squash";

export const useTaskPullRequest = (
  taskId: string | null,
): {
  loading: boolean;
  mergeRequest: ApiPullRequestAttempt | null;
  pullRequest: ApiPullRequest | null;
  rebaseRequest: ApiPullRequestAttempt | null;
  refetch: () => void;
  request: ApiPullRequestAttempt | null;
} => {
  const { data, loading, refetch } = useResource<PullRequestResponse | null>(
    taskId ? `/tasks/${taskId}/pull-request` : null,
  );
  // The PR's state (open → merged), head commit, and title move via a
  // background sync/merge/open. Those fan out `pull_request.updated` (and
  // `task.updated` when the sync also flips the task status); refetch on either
  // so the panel isn't stale — including a new commit on a still-open PR, which
  // only fires `pull_request.updated`.
  //
  // `intent.updated` is here for the merge intent specifically: a merge that
  // fails moves no pull request and no task, so it fans out nothing else — and
  // the button would sit on "Merging…" until something unrelated happened to
  // refetch.
  useRealtimeScope(taskId ? `task:${taskId}` : null, (event) => {
    if (
      event.kind === "pull_request.updated" ||
      event.kind === "task.updated" ||
      event.kind === "intent.updated" ||
      event.kind === "resync"
    ) {
      refetch();
    }
  });
  return {
    loading,
    mergeRequest: data?.merge_request ?? null,
    pullRequest: data?.pull_request ?? null,
    rebaseRequest: data?.rebase_request ?? null,
    refetch,
    request: data?.request ?? null,
  };
};

// Each of these enqueues a server intent and returns 202; the result lands a
// moment later, so callers refetch the pull request after acting. For merge,
// what the intent went on to do is reported by `merge_request` above — the 202
// only says the request was accepted.
//
// Opening a PR is deliberately not here: the dashboard has no open-PR control
// at all. The agent opens its own pull request via the `open_pull_request`
// tool when its work is done, and that tool call is what enqueues the intent.
// The HTTP route still exists for other API consumers
// (diffs-and-pull-requests.md §Open PR).
export const mergePullRequest = (
  prPublicId: string,
  strategy?: MergeStrategy,
): Promise<unknown> =>
  api.post(`/pull-requests/${prPublicId}/merge`, strategy ? { strategy } : {});

export const syncPullRequest = (prPublicId: string): Promise<unknown> =>
  api.post(`/pull-requests/${prPublicId}/sync`, {});

// Arm or disarm the automatic merge. Unlike the two above this settles inline —
// the flag is stored before the response — but the merge it may trigger is
// still an intent, reported by `merge_request` like any other.
export const setMergeWhenReady = (
  prPublicId: string,
  enabled: boolean,
): Promise<unknown> =>
  api.put(`/pull-requests/${prPublicId}/merge-when-ready`, { enabled });
